import { ArrowPathIcon, Cog6ToothIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";

import { apiErrorMessage, apiFieldErrors } from "../services/api";
import {
  bulkAlerts,
  fetchAlert,
  fetchAlertAssignees,
  fetchAlerts,
  fetchAlertSettings,
  refreshAlerts,
  saveAlertSettings,
  updateAlert,
} from "../services/alertService";
import { getCurrentUser } from "../services/authService";
import { hasFeature } from "../services/features";
import { can } from "../services/permissions";
import { fetchReportFilterOptions } from "../services/reportService";
import { formatDateTime } from "../services/datetime";
import { labelFor } from "../services/enumLabels";

const TYPES = [
  "out_of_stock",
  "low_stock",
  "stale_booking",
  "unpaid_visit",
  "settlement_overdue",
  "schedule_conflict",
  "low_rating",
  "adjustment_exception",
];
const STOCK_TYPES = ["out_of_stock", "low_stock"];
// Stock rules need the plan's inventory; without it the API hides their alerts.
const alertTypes = () =>
  hasFeature(getCurrentUser(), "inventory") ? TYPES : TYPES.filter((type) => !STOCK_TYPES.includes(type));
const SEVERITIES = ["critical", "high", "medium", "low"];
const STATUSES = ["active", "open", "acknowledged", "resolved", "dismissed"];

const DOT = { critical: "bg-danger", high: "bg-danger", medium: "bg-warning", low: "bg-ink-2" };

/** "3h", "2d" — how long an alert has been open. */
const age = (minutes, t) => {
  if (minutes === null || minutes === undefined) return "—";
  if (minutes < 60) return t("alerts.age.minutes", { count: minutes });
  if (minutes < 60 * 48) return t("alerts.age.hours", { count: Math.floor(minutes / 60) });
  return t("alerts.age.days", { count: Math.floor(minutes / 1440) });
};

/**
 * Managed alerts (spec 08): what needs a person, routed by the server to this
 * reader. Filters live in the URL (`?type=…&severity=…&status=…&branch=…&assignee=…`),
 * so the dashboard's "Needs attention" rows and notifications deep-link here.
 */
export default function Alerts() {
  const { t, i18n } = useTranslation();
  const user = getCurrentUser();
  const mayManage = can(user, "alerts.manage");
  const maySettings = can(user, "settings.alerts");
  const [params, setParams] = useSearchParams();
  const [state, setState] = useState({ loading: true, rows: [], meta: null, error: "" });
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState([]);
  const [branches, setBranches] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [busy, setBusy] = useState("");
  const [resolving, setResolving] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [reload, setReload] = useState(0);

  const focus = params.get("alert");
  const filters = useMemo(() => {
    const status = params.get("status") || "active";

    return {
      type: params.get("type") ? params.get("type").split(",") : [],
      severity: params.get("severity") ? params.get("severity").split(",") : [],
      status: status === "active" ? [] : [status],
      branches: params.get("branch") || "",
      assignee: params.get("assignee") || "",
    };
  }, [params]);

  const setFilter = (name, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(name, value);
    else next.delete(name);
    next.delete("alert");
    setParams(next);
    setPage(1);
  };

  useEffect(() => {
    fetchReportFilterOptions()
      .then((o) => setBranches(o.branches ?? []))
      .catch(() => {});
    if (mayManage)
      fetchAlertAssignees()
        .then(setAssignees)
        .catch(() => {});
  }, [mayManage]);

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: "" }));

    // A deep link to one alert (`?alert=`, e.g. from a notification) shows just it.
    (focus ? fetchAlert(focus).then((alert) => ({ data: [alert], meta: null })) : fetchAlerts(filters, page))
      .then((body) => {
        if (cancelled) return;
        setState({ loading: false, rows: body.data, meta: body.meta, error: "" });
        setSelected([]);
      })
      .catch(
        (err) =>
          !cancelled &&
          setState({
            loading: false,
            rows: [],
            meta: null,
            error: apiErrorMessage(err, t("alerts.loadFailed")),
          })
      );

    return () => {
      cancelled = true;
    };
  }, [filters, page, focus, reload, i18n.language, t]);

  const act = useCallback(
    async (alert, payload) => {
      setBusy(alert.id);
      try {
        await updateAlert(alert.id, payload);
        setReload((n) => n + 1);
      } catch (err) {
        setState((s) => ({ ...s, error: apiErrorMessage(err, t("alerts.actionFailed")) }));
      } finally {
        setBusy("");
      }
    },
    [t]
  );

  const bulk = async (action) => {
    setBusy("bulk");
    try {
      await bulkAlerts(selected, action);
      setReload((n) => n + 1);
    } catch (err) {
      setState((s) => ({ ...s, error: apiErrorMessage(err, t("alerts.actionFailed")) }));
    } finally {
      setBusy("");
    }
  };

  const runRefresh = async () => {
    setBusy("refresh");
    try {
      await refreshAlerts();
      setReload((n) => n + 1);
    } catch (err) {
      setState((s) => ({ ...s, error: apiErrorMessage(err, t("alerts.actionFailed")) }));
    } finally {
      setBusy("");
    }
  };

  const { loading, rows, meta, error } = state;
  const selectable = rows.filter((a) => a.status === "open" || a.status === "acknowledged");
  const select = "field-input h-9 w-auto py-0 text-dense";

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">{t("alerts.title")}</h1>
          <p className="text-sm text-ink-2">{t("alerts.subtitle")}</p>
        </div>
        <div className="flex gap-2">
          {mayManage && (
            <button
              type="button"
              className="btn-secondary inline-flex items-center gap-1.5"
              onClick={runRefresh}
              disabled={busy === "refresh"}
            >
              <ArrowPathIcon
                className={`h-4 w-4 ${busy === "refresh" ? "animate-spin" : ""}`}
                aria-hidden="true"
              />
              {t("alerts.refresh")}
            </button>
          )}
          {maySettings && (
            <button
              type="button"
              className="btn-secondary inline-flex items-center gap-1.5"
              onClick={() => setSettingsOpen(true)}
            >
              <Cog6ToothIcon className="h-4 w-4" aria-hidden="true" />
              {t("alerts.settings.title")}
            </button>
          )}
        </div>
      </div>

      {focus ? (
        <p className="mb-3 text-dense">
          <button type="button" className="text-accent-text underline" onClick={() => setFilter("alert", "")}>
            {t("alerts.showAll")}
          </button>
        </p>
      ) : (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <select
            className={select}
            value={filters.type[0] ?? ""}
            onChange={(e) => setFilter("type", e.target.value)}
            aria-label={t("alerts.filters.type")}
          >
            <option value="">{t("alerts.filters.allTypes")}</option>
            {alertTypes().map((x) => (
              <option key={x} value={x}>
                {labelFor("alert_type", x)}
              </option>
            ))}
          </select>
          <select
            className={select}
            value={filters.severity[0] ?? ""}
            onChange={(e) => setFilter("severity", e.target.value)}
            aria-label={t("alerts.filters.severity")}
          >
            <option value="">{t("alerts.filters.allSeverities")}</option>
            {SEVERITIES.map((x) => (
              <option key={x} value={x}>
                {labelFor("alert_severity", x)}
              </option>
            ))}
          </select>
          <select
            className={select}
            value={params.get("status") || "active"}
            onChange={(e) => setFilter("status", e.target.value === "active" ? "" : e.target.value)}
            aria-label={t("alerts.filters.status")}
          >
            {STATUSES.map((x) => (
              <option key={x} value={x}>
                {labelFor("alert_status", x)}
              </option>
            ))}
          </select>
          {branches.length > 1 && (
            <select
              className={select}
              value={filters.branches}
              onChange={(e) => setFilter("branch", e.target.value)}
              aria-label={t("alerts.filters.branch")}
            >
              <option value="">{t("alerts.filters.allBranches")}</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          )}
          <select
            className={select}
            value={filters.assignee}
            onChange={(e) => setFilter("assignee", e.target.value)}
            aria-label={t("alerts.filters.assignee")}
          >
            <option value="">{t("alerts.filters.anyone")}</option>
            <option value="me">{t("alerts.filters.me")}</option>
            <option value="none">{t("alerts.filters.unassigned")}</option>
            {assignees.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
          {mayManage && selected.length > 0 && (
            <button
              type="button"
              className="btn-primary h-9"
              disabled={busy === "bulk"}
              onClick={() => bulk("acknowledge")}
            >
              {t("alerts.bulkAcknowledge", { count: selected.length })}
            </button>
          )}
        </div>
      )}

      {error && (
        <div className="mb-3 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
          {error}
        </div>
      )}

      <div className="card overflow-hidden">
        {loading ? (
          <div className="space-y-2 p-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-14 animate-pulse rounded-lg bg-raised" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <p className="p-10 text-center text-ink-2">{t("alerts.empty")}</p>
        ) : (
          <ul className="divide-y divide-line">
            {mayManage && selectable.length > 0 && (
              <li className="flex items-center gap-3 bg-raised px-4 py-2 text-caption text-ink-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[var(--pl-accent)]"
                  checked={selected.length === selectable.length}
                  onChange={(e) => setSelected(e.target.checked ? selectable.map((a) => a.id) : [])}
                  aria-label={t("alerts.selectAll")}
                />
                {t("alerts.selectAll")}
              </li>
            )}
            {rows.map((alert) => {
              const live = alert.status === "open" || alert.status === "acknowledged";

              return (
                <li
                  key={alert.id}
                  className={`flex flex-wrap items-start gap-3 px-4 py-3 ${focus === alert.id ? "bg-accent-wash" : ""}`}
                >
                  {mayManage && (
                    <input
                      type="checkbox"
                      className="mt-1.5 h-4 w-4 accent-[var(--pl-accent)]"
                      disabled={!live}
                      checked={selected.includes(alert.id)}
                      onChange={(e) =>
                        setSelected((s) =>
                          e.target.checked ? [...s, alert.id] : s.filter((id) => id !== alert.id)
                        )
                      }
                      aria-label={alert.title}
                    />
                  )}
                  <span
                    className={`mt-2 h-2.5 w-2.5 flex-shrink-0 rounded-full ${DOT[alert.severity]}`}
                    title={alert.severity_label}
                    aria-hidden="true"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold">{alert.title}</p>
                    {alert.body && <p className="text-dense text-ink-2">{alert.body}</p>}
                    <p className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-caption text-ink-2">
                      <span>{alert.type_label}</span>
                      <span>{alert.severity_label}</span>
                      {alert.branch && <span>{alert.branch.name}</span>}
                      <span>{t("alerts.ageLabel", { age: age(alert.age_minutes, t) })}</span>
                      {alert.due_at && (
                        <span className={alert.overdue ? "font-semibold text-danger-text" : ""}>
                          {t("alerts.due", { date: formatDateTime(alert.due_at, i18n.language) })}
                        </span>
                      )}
                      <span>
                        {alert.assignee
                          ? t("alerts.assignedTo", { name: alert.assignee.name })
                          : alert.owner_label}
                      </span>
                      <span className="font-semibold">
                        {alert.auto_resolved ? t("alerts.autoResolved") : alert.status_label}
                      </span>
                    </p>
                    {alert.resolution_note && !alert.auto_resolved && (
                      <p className="mt-1 text-caption italic text-ink-2">{alert.resolution_note}</p>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {alert.source?.path && (
                      <Link to={alert.source.path} className="btn-secondary h-8 px-3 text-dense">
                        {t("alerts.openSource")}
                      </Link>
                    )}
                    {mayManage && alert.status === "open" && (
                      <button
                        type="button"
                        className="btn-secondary h-8 px-3 text-dense"
                        disabled={busy === alert.id}
                        onClick={() => act(alert, { action: "acknowledge" })}
                      >
                        {t("alerts.actions.acknowledge")}
                      </button>
                    )}
                    {mayManage && live && assignees.length > 0 && (
                      <select
                        className="field-input h-8 w-auto py-0 text-dense"
                        value={alert.assignee?.id ?? ""}
                        disabled={busy === alert.id}
                        onChange={(e) =>
                          act(alert, { action: "assign", assignee_user_id: e.target.value || null })
                        }
                        aria-label={t("alerts.actions.assign")}
                      >
                        <option value="">{t("alerts.filters.unassigned")}</option>
                        {assignees.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name}
                          </option>
                        ))}
                      </select>
                    )}
                    {mayManage && live && (
                      <button
                        type="button"
                        className="btn-primary h-8 px-3 text-dense"
                        disabled={busy === alert.id}
                        onClick={() => setResolving(alert)}
                      >
                        {t("alerts.actions.resolve")}
                      </button>
                    )}
                    {mayManage && live && (
                      <button
                        type="button"
                        className="h-8 px-2 text-dense text-ink-2 hover:text-ink"
                        disabled={busy === alert.id}
                        onClick={() => act(alert, { action: "dismiss" })}
                      >
                        {t("alerts.actions.dismiss")}
                      </button>
                    )}
                    {mayManage && alert.status === "dismissed" && (
                      <button
                        type="button"
                        className="btn-secondary h-8 px-3 text-dense"
                        disabled={busy === alert.id}
                        onClick={() => act(alert, { action: "reopen" })}
                      >
                        {t("alerts.actions.reopen")}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {meta && meta.last_page > 1 && !focus && (
          <div className="flex items-center justify-between border-t border-line px-4 py-3 text-dense text-ink-2">
            <span>{t("alerts.count", { count: meta.total })}</span>
            <div className="flex gap-2">
              <button
                type="button"
                className="btn-secondary h-8 px-3"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                {t("alerts.previous")}
              </button>
              <button
                type="button"
                className="btn-secondary h-8 px-3"
                disabled={page >= meta.last_page}
                onClick={() => setPage(page + 1)}
              >
                {t("alerts.next")}
              </button>
            </div>
          </div>
        )}
      </div>

      {resolving && (
        <ResolveDialog
          alert={resolving}
          onClose={() => setResolving(null)}
          onResolve={async (note) => {
            await act(resolving, { action: "resolve", note: note || null });
            setResolving(null);
          }}
        />
      )}
      {settingsOpen && (
        <SettingsDialog onClose={() => setSettingsOpen(false)} onSaved={() => setReload((n) => n + 1)} />
      )}
    </div>
  );
}

function Dialog({ title, onClose, children }) {
  const { t } = useTranslation();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div role="dialog" aria-modal="true" className="w-full max-w-lg rounded-xl bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-line px-5 py-3">
          <h2 className="text-sm font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("alerts.close")}
            className="rounded p-1 text-ink-muted hover:bg-raised hover:text-ink-2"
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ResolveDialog({ alert, onClose, onResolve }) {
  const { t } = useTranslation();
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  return (
    <Dialog title={t("alerts.resolveTitle")} onClose={onClose}>
      <div className="space-y-3 px-5 py-4">
        <p className="text-dense">{alert.title}</p>
        <label className="field-label" htmlFor="alert-note">
          {t("alerts.note")}
        </label>
        <textarea
          id="alert-note"
          className="field-input"
          rows={3}
          maxLength={1000}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <p className="text-caption text-ink-2">{t("alerts.resolveHint")}</p>
      </div>
      <div className="flex justify-end gap-2 border-t border-line px-5 py-3">
        <button type="button" className="btn-secondary" onClick={onClose}>
          {t("alerts.cancel")}
        </button>
        <button
          type="button"
          className="btn-primary"
          disabled={saving}
          onClick={async () => {
            setSaving(true);
            await onResolve(note.trim());
          }}
        >
          {t("alerts.actions.resolve")}
        </button>
      </div>
    </Dialog>
  );
}

const NUMBERS = [
  "stale_after_minutes",
  "low_rating_max",
  "low_rating_escalate_hours",
  "low_rating_lookback_days",
  "settlement_sla_business_days",
  "loyalty_points_limit",
  "loyalty_stamps_limit",
  "adjustment_lookback_days",
];

function SettingsDialog({ onClose, onSaved }) {
  const { t } = useTranslation();
  const [values, setValues] = useState(null);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAlertSettings()
      .then((data) => setValues(data.settings))
      .catch((err) => setError(apiErrorMessage(err, t("alerts.loadFailed"))));
  }, [t]);

  const save = async () => {
    setSaving(true);
    setError("");
    setErrors({});
    try {
      const payload = {
        ...values,
        refund_limit_amount: values.refund_limit_amount === "" ? null : values.refund_limit_amount,
      };
      await saveAlertSettings(payload);
      onSaved();
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, t("alerts.actionFailed")));
      setErrors(apiFieldErrors(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog title={t("alerts.settings.title")} onClose={onClose}>
      <div className="max-h-[70vh] space-y-4 overflow-y-auto px-5 py-4">
        {error && (
          <div className="rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
            {error}
          </div>
        )}
        {!values ? (
          <div className="h-40 animate-pulse rounded-lg bg-raised" />
        ) : (
          <fieldset disabled={saving} className="space-y-4">
            <div>
              <p className="field-label">{t("alerts.settings.rules")}</p>
              <div className="grid gap-1 sm:grid-cols-2">
                {alertTypes().map((type) => (
                  <label key={type} className="flex items-center gap-2 text-dense">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-[var(--pl-accent)]"
                      checked={values.enabled?.[type] !== false}
                      onChange={(e) =>
                        setValues((v) => ({
                          ...v,
                          enabled: { ...(v.enabled ?? {}), [type]: e.target.checked },
                        }))
                      }
                    />
                    {labelFor("alert_type", type)}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {NUMBERS.map((key) => (
                <label key={key} className="text-dense">
                  <span className="field-label">{t(`alerts.settings.${key}`)}</span>
                  <input
                    type="number"
                    min={0}
                    className="field-input"
                    value={values[key] ?? ""}
                    onChange={(e) =>
                      setValues((v) => ({
                        ...v,
                        [key]: e.target.value === "" ? null : Number(e.target.value),
                      }))
                    }
                  />
                  {errors[key]?.[0] && <span className="field-error">{errors[key][0]}</span>}
                </label>
              ))}
              <label className="text-dense">
                <span className="field-label">{t("alerts.settings.refund_limit_amount")}</span>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  className="field-input"
                  value={values.refund_limit_amount ?? ""}
                  onChange={(e) => setValues((v) => ({ ...v, refund_limit_amount: e.target.value }))}
                  placeholder={t("alerts.settings.refundLimitNone")}
                />
              </label>
            </div>
          </fieldset>
        )}
      </div>
      <div className="flex justify-end gap-2 border-t border-line px-5 py-3">
        <button type="button" className="btn-secondary" onClick={onClose}>
          {t("alerts.cancel")}
        </button>
        <button type="button" className="btn-primary" disabled={!values || saving} onClick={save}>
          {t("alerts.save")}
        </button>
      </div>
    </Dialog>
  );
}
